import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useSetRecoilState } from 'recoil';

import { COLOR, LAYOUT, Variation } from '../../../types/globalSearch';

import styled from '@emotion/styled';
import Dialog from '../../../designSystem/components/Dialog';
import { globalVariables } from '../../../designSystem/constants';

import { AdsShrinkingBanner, CookieKey } from '@myrealtrip/ads';
import { useEnvContext } from '../../../../modules/EnvProvider';
import { fetchInteractiveBanner } from '../../../api/ads';
import CookieManager from '../../../utils/cookieManager';
import SearchInput from '../SearchInput';
import SearchSuggestionContent from '../SearchSuggestionContent';
import useSearchOffers from '../hooks/apis/useSearchOffers';
import { searchTerm } from '../recoil';

interface Props {
  isOpen: boolean;
  variations: Variation;
  placeholder: string;
  onDialogToggle: (isOpen: boolean) => void;
  onPressKeyboard: (key: string) => void;
  onSearch: () => void;
}

function SearchSuggestionDialog({
  isOpen,
  variations,
  placeholder,
  onDialogToggle,
  onPressKeyboard,
  onSearch,
}: Props) {
  const queryClient = useQueryClient();

  const { targetEnv } = useEnvContext();
  const { data, isError } = useSearchOffers();
  const sections = isError ? [] : data?.sections || [];
  const isEmpty = sections.length < 1;

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const setSearchKeyword = useSetRecoilState(searchTerm);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const inventoryId = CookieManager.get(CookieKey.INTERACTIVE_BANNER_ID) || '';

  const { data: bannerData } = useQuery(
    ['fetchInteractiveBanner', { targetEnv, inventoryId }],
    () =>
      fetchInteractiveBanner(
        targetEnv,
        'search_home',
        'INTERACTIVE_BANNER_IMAGE_AD_CONTAINER_1',
        inventoryId,
      ),
    {
      enabled: isOpen,
    },
  );

  const banner = bannerData?.data?.data?.inventories?.[0];

  const defaultVariations: Variation = {
    layout: LAYOUT.header,
    color: COLOR.white,
  };

  const handleDialogClose = () => {
    setSearchKeyword('');
    onDialogToggle(false);
  };

  const handleClearInput = () => {
    hiddenInputRef.current?.focus();
    searchInputRef.current?.focus();
  };

  const handleBannerClose = () => {
    queryClient.removeQueries({
      queryKey: ['fetchInteractiveBanner', { targetEnv, inventoryId }],
    });
  };

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <DialogContainer
      isOpen={isOpen}
      titleText=" "
      toggle={() => {}}
      onCloseClick={handleDialogClose}
    >
      <SearchInputContainer>
        <SearchInput
          ref={searchInputRef}
          variations={defaultVariations}
          placeholder={placeholder}
          popupSide
          onDialogToggle={onDialogToggle}
          onPressKeyboard={onPressKeyboard}
          onClearInput={handleClearInput}
          onSearch={onSearch}
        />
        <HiddenInput tabIndex={-1} ref={hiddenInputRef} />
      </SearchInputContainer>
      {isEmpty ? (
        <AdsShrinkingBanner
          banner={banner}
          animationMode="FIX"
          initialStep="COMPACT"
          onBannerClose={handleBannerClose}
          logData={{
            screenName: 'search_home',
            mylink_id: '',
          }}
        />
      ) : (
        <SearchSuggestionContentContainer>
          <SearchSuggestionContent variations={variations} />
        </SearchSuggestionContentContainer>
      )}
    </DialogContainer>
  );
}

export default SearchSuggestionDialog;

const DialogContainer = styled(Dialog.Mobile)`
  .mrt-dialog-content {
    padding: 0;
  }
  .mrt-dialog-title {
    div:first-of-type {
      border-bottom: 0;
    }
  }

  z-index: ${globalVariables.z_dialog}; // var(--z-dialog)
`;

const SearchInputContainer = styled.div`
  position: relative;
  margin-top: 8px;
  padding: 0 16px;
`;

const SearchSuggestionContentContainer = styled.div`
  margin-top: 8px;
`;

const HiddenInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  opacity: 0;
`;
